import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import { useNotifications } from "./useNotifications";
import { friendlyMessage, getMyActivity } from "./projectApi";
import type { MyActivity } from "../models/project";

const EMPTY: MyActivity = {
  myIssues: [],
  mentionedPosts: [],
  mentionedInComments: [],
  myPosts: [],
  activityOnMyPosts: [],
  myChats: [],
};

// Data for the account-wide "내 활동" surface: cross-project issues/blog/chat activity, plus the
// notification feed borrowed from useNotifications (shared with 홈, so mark-read behaves identically
// on both screens).
export function useMyActivity() {
  const { status, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const notifs = useNotifications();
  const [activity, setActivity] = useState<MyActivity>(EMPTY);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reloadActivity = useCallback(async () => {
    setLoadingActivity(true);
    setError(null);
    try {
      setActivity(await getMyActivity(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoadingActivity(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (status === "authenticated") void reloadActivity();
  }, [status, reloadActivity]);

  return {
    activity,
    notifications: notifs.notifications,
    unreadCount: notifs.unreadCount,
    loading: loadingActivity || notifs.loading,
    error: error ?? notifs.error,
    clearError: () => {
      setError(null);
      notifs.clearError();
    },
    reload: async () => {
      await Promise.all([reloadActivity(), notifs.reload()]);
    },
    markRead: notifs.markRead,
    markAllRead: notifs.markAllRead,
  };
}
